import React, { useEffect, useState } from 'react';
import { Button, Empty, Spin, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { showError } from '../../../utils/exceptionUtils';
import { ReportFileTypeEnum } from '../../../enums/ReportFileEnum';
import SectionCard from '../activity/components/SectionCard';
import {
    DriverActivityRiskLogInput,
    EXPORT_DRIVER_ACTIVITY_RISK_LOG,
    ExportDriverActivityRiskLogResponse,
    ExportDriverActivityRiskLogVariables,
    PAGE_DRIVER_ACTIVITY_RISK_LOG,
    PageDriverActivityRiskLogResponse,
    PageDriverActivityRiskLogVariables,
} from './graphql/queries';
import { EMPTY_MARK, RISK_LOG_PAGE_SIZE, buildRiskLogText, formatLaTime, groupByLaDate, parseSearchParams } from './utils/riskLogUtils';

/**
 * 风控日志结果页：条件全部来自 URL；按美西日期分组展示，每页 100 条，同一天跨页时重复日期头。
 */
const RiskLogResultPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const params = parseSearchParams(searchParams);
    const [page, setPage] = useState(1);

    useEffect(() => setPage(1), [location.search]);

    const input: DriverActivityRiskLogInput | null = params && {
        driverId: params.driverId ? Number(params.driverId) : null,
        phone: params.phone ?? null,
        startDate: params.startDate,
        endDate: params.endDate,
    };

    const { data, loading } = useQuery<PageDriverActivityRiskLogResponse, PageDriverActivityRiskLogVariables>(PAGE_DRIVER_ACTIVITY_RISK_LOG, {
        variables: { input: input!, page, size: RISK_LOG_PAGE_SIZE },
        skip: !input,
        fetchPolicy: 'network-only',
        onError: showError,
    });
    const [exportRiskLog, { loading: exporting }] = useMutation<ExportDriverActivityRiskLogResponse, ExportDriverActivityRiskLogVariables>(
        EXPORT_DRIVER_ACTIVITY_RISK_LOG
    );

    if (!input) return <Navigate to="/partner-control/activityRisk/risk-log" replace />;

    const result = data?.pageDriverActivityRiskLog;
    const groups = groupByLaDate(result?.items ?? []);
    const backToSearch = () => navigate(`/partner-control/activityRisk/risk-log${location.search}`);

    const handleExport = async () => {
        try {
            const res = await exportRiskLog({ variables: { input, language: i18n.language.split('-')[0] } });
            const exportResult = res.data?.exportDriverActivityRiskLog;
            if (exportResult?.success) {
                message.success(t('driverOps.riskLog.export.success'));
                navigate(`/partner-control/download?type=${ReportFileTypeEnum.DRIVER_ACTIVITY_RISK_LOG_EXPORT}`);
            } else if (exportResult?.errorCode === 'NO_DATA') {
                message.warning(t('driverOps.riskLog.export.noData'));
            } else if (exportResult?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = exportResult.errorCode.split(':')[1] ?? '';
                message.error(t('driverOps.riskLog.export.exceedMaxLimit', { max }));
            } else {
                message.error(t('driverOps.riskLog.export.failed'));
            }
        } catch (e) {
            showError(e);
        }
    };

    return (
        <div>
            <div className="text-[13px] text-[#8C8C8C] mb-2">
                <span>{t('driverOps.menu.activityRisk')}</span>
                {' / '}
                <span className="cursor-pointer" onClick={backToSearch}>
                    {t('driverOps.menu.riskLog')}
                </span>
                {' / '}
                <span className="text-[#595959]">{t('driverOps.riskLog.result.title')}</span>
            </div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-[26px] font-bold m-0">{t('driverOps.riskLog.result.title')}</h2>
                <div className="flex gap-2">
                    <Button onClick={backToSearch}>{t('driverOps.riskLog.result.modifyQuery')}</Button>
                    <Button type="primary" loading={exporting} onClick={handleExport}>
                        {t('driverOps.riskLog.result.export')}
                    </Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {result?.driver && (
                    <div className="bg-white border border-[#EFEFEF] rounded-[10px] px-[22px] py-4 mb-[18px] flex gap-6 items-center">
                        <span className="text-[13px] text-[#8C8C8C]">{t('driverOps.riskLog.result.driver')}</span>
                        <span className="font-semibold">
                            {result.driver.accountId} · {result.driver.name}
                        </span>
                        <span className="text-[#595959]">{result.driver.maskedPhone || EMPTY_MARK}</span>
                    </div>
                )}

                <SectionCard
                    title={t('driverOps.riskLog.title')}
                    extra={<span className="text-[13px] text-[#8C8C8C]">{t('driverOps.riskLog.result.sortHint')}</span>}
                >
                    {groups.length === 0 ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        groups.map((group) => (
                            <div key={group.date} className="mb-4 last:mb-0">
                                <div className="font-semibold text-[#1F1F1F] mb-2">
                                    {t('driverOps.riskLog.result.dateHeader', {
                                        date: group.date,
                                        weekday: t(`enum.weekdaysOptions.${group.weekdayKey}`),
                                    })}
                                </div>
                                {group.items.map((item, index) => {
                                    const text = buildRiskLogText(item);
                                    return (
                                        <div key={`${item.changedAt}-${index}`} className="flex gap-4 py-[6px] text-[13px]">
                                            <span className="text-[#8C8C8C] tabular-nums">{formatLaTime(item.changedAt)}</span>
                                            <span className="text-[#262626]">{text ? t(text.key, text.params) : item.changeType}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                    {(result?.total ?? 0) > 0 && (
                        <TablePaginationBar
                            total={result!.total}
                            current={page}
                            pageSize={RISK_LOG_PAGE_SIZE}
                            showSizeChanger={false}
                            onChange={(next) => setPage(next)}
                            className="pt-[14px]"
                        />
                    )}
                </SectionCard>
            </Spin>

            <div className="text-[12px] text-[#8C8C8C] leading-5">{t('driverOps.riskLog.result.rulesNote')}</div>
        </div>
    );
};

export default RiskLogResultPage;
